import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isOwner, setIsOwner] = useState(false);
  const [ownerData, setOwnerData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();

  // Validate session on mount
  useEffect(() => {
    const validateSession = async () => {
      const sessionToken = localStorage.getItem("sessionToken");

      if (!sessionToken) {
        setIsInitialized(true);
        return;
      }

      try {
        const response = await fetch("/api/auth/validateSession", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionToken }),
        });

        if (response.ok) {
          const data = await response.json();
          const userData = {
            userId: data.userId,
            userName: data.userName,
            email: data.email,
          };
          setOwnerData(userData);
          setIsOwner(true);
          // Persist for non-React code (PoolModalHelper, etc.)
          localStorage.setItem("userId", data.userId);
          localStorage.setItem("userName", data.userName);
          localStorage.setItem("email", data.email);
        } else {
          // Session invalid or expired -- clean up
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("userId");
          localStorage.removeItem("userName");
          localStorage.removeItem("email");
          setIsOwner(false);
          setOwnerData(null);
        }
      } catch (error) {
        console.error("Session validation failed:", error);
        localStorage.removeItem("sessionToken");
      } finally {
        setIsInitialized(true);
      }
    };

    validateSession();
  }, []);

  // Re-validate when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("sessionToken")) {
        // Re-validate silently
        fetch("/api/auth/validateSession", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionToken: localStorage.getItem("sessionToken") }),
        }).then(response => {
          if (!response.ok) {
            localStorage.removeItem("sessionToken");
            setIsOwner(false);
            setOwnerData(null);
          }
        }).catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const login = useCallback(async (sessionToken, userData) => {
    localStorage.setItem("sessionToken", sessionToken);
    localStorage.setItem("userId", userData.userId);
    localStorage.setItem("userName", userData.userName);
    localStorage.setItem("email", userData.email);
    setOwnerData({
      userId: userData.userId,
      userName: userData.userName,
      email: userData.email,
    });
    setIsOwner(true);
    navigate("/home", { replace: true });
  }, [navigate]);

  const logout = useCallback(async () => {
    const sessionToken = localStorage.getItem("sessionToken");

    if (sessionToken) {
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionToken }),
        });
      } catch (error) {
        // Logout API failure is non-critical
      }
    }

    localStorage.removeItem("sessionToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("email");
    setIsOwner(false);
    setOwnerData(null);
    navigate("/auth/login", { replace: true });
  }, [navigate]);

  const value = React.useMemo(() => ({
    isOwner,
    ownerData,
    isInitialized,
    login,
    logout,
    // Backwards compat aliases during migration
    isLoggedIn: isOwner,
  }), [isOwner, ownerData, isInitialized, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const ProtectedRoute = ({ children }) => {
  const { isOwner, isInitialized } = useAuth();
  const location = useLocation();

  if (!isInitialized) {
    return null; // Don't render anything until session is validated
  }

  if (!isOwner) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return children;
};
